<template>
  <v-container class="pa-0 modern-detail-view">
    <!-- Progress Indicator for Loading -->
    <v-progress-linear
      v-show="loading || internalLoading"
      color="primary"
      indeterminate
      rounded
      height="4"
      class="sticky-progress shadow-sm"
    />

    <!-- Background Refresh Overlay -->
    <v-fade-transition>
      <div v-if="internalLoading" class="refresh-overlay">
        <div class="d-flex flex-column align-center pa-6 rounded-xl bg-white shadow-lg border">
          <v-progress-circular indeterminate color="primary" size="40" width="4" class="mb-4"></v-progress-circular>
          <div class="text-subtitle-2 font-weight-black primary--text text-uppercase tracking-widest">
            Sincronizando Datos...
          </div>
          <div class="caption grey--text font-weight-medium">Actualizando métricas y bitácora</div>
        </div>
      </div>
    </v-fade-transition>

    <div class="pa-6" v-if="work_order && work_order.id" :class="{'opacity-40 pointer-events-none': internalLoading}">
      <!-- Premium Header Section -->
      <v-row class="mb-8 align-start">
        <v-col cols="12" md="8">
          <div class="d-flex align-center flex-wrap gap-3 mb-2">
            <h1 class="text-h3 font-weight-black primary--text mr-4">
              OT #{{ work_order.id }}
            </h1>
            <v-chip
              color="primary lighten-4"
              text-color="primary darken-2"
              label
              class="font-weight-black text-uppercase shadow-sm px-4"
            >
              <v-icon x-small left color="primary darken-2">fas fa-industry</v-icon>
              {{ work_order.completed ? 'Orden Finalizada' : 'En Producción' }}
            </v-chip>
            <v-chip v-if="delayed" color="error lighten-4" text-color="error darken-2" label class="font-weight-black shadow-sm">
              <v-icon x-small left color="error darken-2">fas fa-exclamation-triangle</v-icon>
              Atrasada
            </v-chip>
          </div>
          
          <div class="d-flex align-center mt-2 flex-wrap gap-4">
            <div class="caption grey--text font-weight-medium d-flex align-center">
              <v-avatar size="24" color="grey lighten-4" class="mr-2">
                <v-icon x-small color="grey darken-1">fas fa-user-plus</v-icon>
              </v-avatar>
              {{ work_order.created_by.first_name }} {{ work_order.created_by.last_name }} 
              <span class="mx-2 opacity-50">•</span>
              {{ work_order.created_at | moment('DD/MM/YYYY HH:mm') }}
            </div>
            <div v-if="work_order.updated_by" class="caption grey--text font-weight-medium d-flex align-center">
              <v-avatar size="24" color="grey lighten-4" class="mr-2">
                <v-icon x-small color="grey darken-1">fas fa-pen-nib</v-icon>
              </v-avatar>
              {{ work_order.updated_by.first_name }} {{ work_order.updated_by.last_name }}
              <span class="mx-2 opacity-50">•</span>
              {{ work_order.updated_at | moment('DD/MM/YYYY HH:mm') }}
            </div>
          </div>
        </v-col>
        
        <v-col cols="12" md="4" class="text-right">
          <div class="d-flex justify-end align-center gap-2">
            <v-menu offset-y left transition="slide-y-transition" rounded="lg">
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon color="primary" v-bind="attrs" v-on="on" class="grey lighten-4 shadow-sm">
                  <v-icon small>fas fa-ellipsis-v</v-icon>
                </v-btn>
              </template>
              <v-list dense class="py-1">
                <v-list-item class="px-3">
                  <print-work-order :workorder="work_order" />
                </v-list-item>
                <v-list-item v-if="$can('readCost', 'SalesNote')" @click="generateCostReport(work_order)" class="px-3">
                  <v-list-item-icon class="mr-3 ml-0"><v-icon small color="blue-grey">fas fa-file-invoice-dollar</v-icon></v-list-item-icon>
                  <v-list-item-title class="body-2 font-weight-medium">Informe de Costos</v-list-item-title>
                </v-list-item>
                
                <v-divider v-if="$can('update', 'WorkOrder')" class="my-1"></v-divider>
                
                <v-list-item v-if="!work_order.completed && $can('update', 'WorkOrder')" @click="update" class="px-3">
                  <v-list-item-icon class="mr-3 ml-0"><v-icon small color="primary">fas fa-edit</v-icon></v-list-item-icon>
                  <v-list-item-title class="body-2 font-weight-medium">Modificar Orden</v-list-item-title>
                </v-list-item>
                
                <v-list-item v-if="$can('update', 'WorkOrder')" @click="addingCost = true" class="px-3">
                  <v-list-item-icon class="mr-3 ml-0"><v-icon small color="orange">fas fa-plus-circle</v-icon></v-list-item-icon>
                  <v-list-item-title class="body-2 font-weight-medium">Agregar Costo Extra</v-list-item-title>
                </v-list-item>
                
                <v-divider v-if="!work_order.completed && $can('update', 'WorkOrder')" class="my-1"></v-divider>
                
                <v-list-item v-if="!work_order.completed && $can('update', 'WorkOrder')" @click="showConfirmCloseOrder = true" :disabled="has_prestage" class="px-3">
                  <v-list-item-icon class="mr-3 ml-0"><v-icon small color="success">fas fa-check-double</v-icon></v-list-item-icon>
                  <v-list-item-title class="body-2 font-weight-medium success--text">Finalizar Orden</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-btn icon @click="$emit('close')" class="grey lighten-4 shadow-sm">
              <v-icon small>fas fa-times</v-icon>
            </v-btn>
          </div>
        </v-col>
      </v-row>

      <!-- Dashboard Progress -->
      <v-card outlined class="modern-card pa-6 mb-8 bg-primary-gradient border-0">
        <v-row align="center">
          <v-col cols="12" md="3" class="text-center text-md-left border-right-white">
            <div class="white--text text-overline opacity-70 mb-1">Avance Global</div>
            <div class="white--text text-h2 font-weight-black">{{ Math.ceil(progressEstimated.progress) }}%</div>
          </v-col>
          <v-col cols="12" md="9" class="pa-6">
            <v-progress-linear
              :value="progressEstimated.progress"
              color="white"
              height="12"
              rounded
              striped
              class="elevation-2"
            />
            <div class="d-flex justify-space-between mt-3">
              <div v-if="show_estimated_work_order" class="white--text caption font-weight-bold d-flex align-center">
                <v-icon x-small color="white" class="mr-2 opacity-70">fas fa-calendar-alt</v-icon>
                ENTREGA ESTIMADA: 
                <span class="ml-2 bg-white primary--text px-2 rounded-sm" v-if="!has_prestage && progressEstimated.estimated > 0">
                  {{ progressEstimated.estimated | moment('DD/MM/YYYY') }}
                </span>
                <span v-else-if="has_prestage" class="ml-2 font-italic">En Pre-Prensa</span>
                <span v-else class="ml-2 font-italic">No definida</span>
              </div>
              <div class="white--text caption font-weight-bold d-flex align-center">
                <v-icon x-small color="white" class="mr-2 opacity-70">fas fa-shopping-cart</v-icon>
                CN #{{ work_order.sales_note }}
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card>

      <!-- Detail Information Cards -->
      <v-row class="mb-8">
        <v-col cols="12" md="4">
          <v-card outlined class="modern-card h-100 overflow-hidden">
            <v-sheet color="grey lighten-4" class="px-6 py-3 border-bottom d-flex align-center">
              <v-icon small color="primary" class="mr-3">fas fa-user-circle</v-icon>
              <span class="text-subtitle-2 font-weight-bold grey--text text--darken-2 text-uppercase">Cliente y Proyecto</span>
            </v-sheet>
            <v-card-text class="pa-6">
              <div class="mb-4">
                <div class="caption grey--text mb-1 uppercase font-weight-bold">Razón Social</div>
                <div class="body-1 font-weight-black secondary--text lh-1-2">{{ work_order.customer_name }}</div>
              </div>
              <div>
                <div class="caption grey--text mb-1 uppercase font-weight-bold">Nombre del Proyecto</div>
                <div class="body-2 font-weight-medium grey--text text--darken-3">{{ work_order.name }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card outlined class="modern-card h-100 overflow-hidden">
            <v-sheet color="grey lighten-4" class="px-6 py-3 border-bottom d-flex align-center">
              <v-icon small color="primary" class="mr-3">fas fa-clock</v-icon>
              <span class="text-subtitle-2 font-weight-bold grey--text text--darken-2 text-uppercase">Tiempos y Logística</span>
            </v-sheet>
            <v-card-text class="pa-6">
              <v-row dense>
                <v-col cols="12" class="mb-3">
                  <div class="caption grey--text mb-1 uppercase font-weight-bold">Entrega Comprometida</div>
                  <div class="body-2 font-weight-black" :class="delayed ? 'error--text' : 'primary--text'">
                    <v-icon x-small :color="delayed ? 'error' : 'primary'" class="mr-1">fas fa-calendar-check</v-icon>
                    {{ work_order.delivery_date | moment('DD/MM/YYYY HH:mm') }}
                  </div>
                </v-col>
                <v-col cols="6" v-if="work_order.install_date">
                  <div class="caption grey--text mb-1 uppercase font-weight-bold">Instalación</div>
                  <div class="caption font-weight-bold grey--text text--darken-3">
                    {{ work_order.install_date | moment('DD/MM/YYYY') }}
                  </div>
                </v-col>
                <v-col cols="6" v-if="work_order.uninstall_date">
                  <div class="caption grey--text mb-1 uppercase font-weight-bold">Desinstalación</div>
                  <div class="caption font-weight-bold grey--text text--darken-3">
                    {{ work_order.uninstall_date | moment('DD/MM/YYYY') }}
                  </div>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card outlined class="modern-card h-100 overflow-hidden">
            <v-sheet color="grey lighten-4" class="px-6 py-3 border-bottom d-flex align-center">
              <v-icon small color="primary" class="mr-3">fas fa-align-left</v-icon>
              <span class="text-subtitle-2 font-weight-bold grey--text text--darken-2 text-uppercase">Observaciones</span>
            </v-sheet>
            <v-card-text class="pa-6">
              <div class="body-2 font-italic grey--text text--darken-2 text-left" v-if="work_order.notes">
                {{ work_order.notes }}
              </div>
              <div class="caption grey--text font-italic text-left py-4" v-else>
                Sin observaciones adicionales
              </div>
              <div v-if="work_order.customer_approval" class="mt-4 pa-3 bg-success-light rounded-sm border-success">
                <div class="caption success--text font-weight-black uppercase mb-1">
                  <v-icon x-small color="success" class="mr-1">fas fa-check-circle</v-icon> Visto Bueno
                </div>
                <div class="caption grey--text text--darken-3 lh-1-2">{{ work_order.approval_notes }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Integrative Document Chips -->
      <v-row class="mb-4" v-if="($store.getters.getCompany.softnet_integration_enabled && $can('read','Softnet')) || ($store.getters.getCompany.kame_integration_enabled && $can('read','Kame'))">
        <v-col cols="12">
          <div class="d-flex align-center gap-2 flex-wrap px-1">
            <softnet-documents-chips v-if="$store.getters.getCompany.softnet_integration_enabled" :work_order_id="work_order.id" />
            <kame-documents-chips v-if="$store.getters.getCompany.kame_integration_enabled" :work_order_id="work_order.id" />
          </div>
        </v-col>
      </v-row>

      <!-- Main Tabs Section -->
      <v-card outlined class="modern-card elevation-0 overflow-hidden">
        <v-tabs v-model="tab" background-color="grey lighten-5" color="primary" grow class="border-bottom">
          <v-tab class="text-none font-weight-bold py-4">
            <v-icon left small>fas fa-box-open</v-icon> Productos
          </v-tab>
          <v-tab v-if="$can('manage', 'WorkOrder')" class="text-none font-weight-bold py-4">
            <v-icon left small>fas fa-plus-square</v-icon> Adicionales
          </v-tab>
          <v-tab v-if="$can('read', 'PurchaseOrder')" class="text-none font-weight-bold py-4">
            <v-icon left small>fas fa-shopping-bag</v-icon> Compras
          </v-tab>
          <v-tab v-if="$can('read', 'Invoice')" class="text-none font-weight-bold py-4">
            <v-icon left small>fas fa-file-invoice</v-icon> Facturas Venta
          </v-tab>
          <v-tab v-if="$can('read', 'PurchaseInvoice')" class="text-none font-weight-bold py-4">
            <v-icon left small>fas fa-receipt</v-icon> Facturas Compra
          </v-tab>
          <v-tab v-if="$can('readCost', 'SalesNote')" class="text-none font-weight-bold py-4">
            <v-icon left small>fas fa-chart-pie</v-icon> Resumen
          </v-tab>
          <v-tab v-if="$can('read', 'WorkOrder')" class="text-none font-weight-bold py-4">
            <v-icon left small>fas fa-folder-open</v-icon> Documentos
          </v-tab>
          <v-tab v-if="relbaseEnabled" class="text-none font-weight-bold py-4">
            <v-icon left small style="color:#35BDC8">fas fa-file-invoice-dollar</v-icon>
            <span style="color:#35BDC8">Relbase</span>
            <v-chip v-if="work_order.relbase_documents && work_order.relbase_documents.length" x-small label class="ml-2 font-weight-black" style="background-color:#35BDC8;color:white;">{{ work_order.relbase_documents.length }}</v-chip>
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
          <!-- Productos Tab -->
          <v-tab-item class="bg-white">
            <v-data-table
              :headers="productsTableHeaders"
              :items="work_order.products"
              :single-expand="true"
              :expanded.sync="expanded"
              item-key="id"
              show-expand
              class="modern-table elevation-0"
              hide-default-footer
              :items-per-page="-1"
            >
              <template v-slot:item.library_product.sku="{ item }">
                <span class="caption grey--text font-weight-bold">{{ item.library_product ? item.library_product.sku : '---' }}</span>
              </template>
              
              <template v-slot:item.name="{ item }">
                <span class="body-2 font-weight-black primary--text">{{ item.name }}</span>
              </template>
              
              <template v-slot:item.qty="{ item }">
                <span class="body-2 font-weight-bold">{{ item.qty }}</span>
              </template>
              
              <template v-slot:item.width="{ item }">
                <span class="body-2 font-weight-medium grey--text text--darken-3">{{ item.width }}</span>
              </template>
              
              <template v-slot:item.height="{ item }">
                <span class="body-2 font-weight-medium grey--text text--darken-3">{{ item.height }}</span>
              </template>
              
              <template v-slot:item.status="{ item }">
                <div class="d-flex align-center justify-center">
                  <v-tooltip bottom max-width="400">
                    <template v-slot:activator="{ on, attrs }">
                      <div v-bind="attrs" v-on="on" class="d-inline-block">
                        <v-chip
                          v-if="item.in_prestage"
                          :color="item.prestage.color"
                          label
                          outlined
                          x-small
                          class="font-weight-black pointer"
                          @click.stop="showPrestageDetail(item)"
                        >
                          {{ item.prestage.name }}
                        </v-chip>
                        <v-chip
                          v-else-if="!item.completed"
                          label
                          dark
                          :color="item.estatus.last_machine != '' ? 'primary' : 'grey lighten-1'"
                          x-small
                          class="font-weight-black"
                        >
                          {{ item.estatus.last_machine != '' ? item.estatus.last_machine : 'PENDIENTE' }}
                        </v-chip>
                        <v-chip v-else label dark color="success" x-small class="font-weight-black">
                          FINALIZADO
                        </v-chip>
                      </div>
                    </template>
                    <div class="pa-2" v-if="item.estatus && item.estatus.bitacora_logs.length > 0">
                      <div class="caption font-weight-bold mb-2 border-bottom pb-1">HISTORIAL DE PRODUCCIÓN</div>
                      <div v-for="(reg, i) in item.estatus.bitacora_logs" :key="i" class="caption mb-1 d-flex justify-space-between gap-4">
                        <span>{{ reg.machine }} - {{ reg.user }}</span>
                        <span class="opacity-70">{{ reg.created_at | moment('DD/MM/YYYY') }}</span>
                      </div>
                    </div>
                    <div class="pa-2 caption" v-else>Sin registros de bitácora todavía</div>
                  </v-tooltip>

                  <!-- Botón para ver trazabilidad técnica de pre-prensa una vez que pasó a producción -->
                  <v-tooltip bottom v-if="!item.in_prestage" content-class="premium-tooltip">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        icon
                        x-small
                        class="ml-2 modern-action-btn"
                        @click.stop="showPrestageDetail(item)"
                      >
                        <v-icon x-small color="blue-grey lighten-1">fas fa-history</v-icon>
                      </v-btn>
                    </template>
                    <span>Ver Trazabilidad Pre-Prensa</span>
                  </v-tooltip>
                </div>
              </template>
              
              <template v-slot:item.progress="{ item }">
                <div class="d-flex align-center">
                  <v-progress-linear
                    :value="item.progress"
                    :color="item.progress >= 100 ? 'success' : 'accent'"
                    height="6"
                    rounded
                    class="flex-grow-1"
                  />
                  <span class="caption font-weight-black ml-2 primary--text">{{ Math.floor(item.progress) }}%</span>
                </div>
              </template>
              
              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="pa-0 expanded-row-container">
                  <div class="pa-6 border-left-primary bg-grey-light">
                    <v-card outlined class="modern-card elevation-2 overflow-hidden bg-white">
                      <product-detail :product="item" @updated="refreshWorkOrder()" :work_order_id="work_order.id" />
                    </v-card>
                  </div>
                </td>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- Adicionales Tab -->
          <v-tab-item v-if="$can('manage', 'WorkOrder')" class="pa-6">
            <v-data-table
              :loading="costLoading"
              :headers="additionalCostsHeaders"
              :items="additionalCosts"
              class="modern-table elevation-0 border-rounded overflow-hidden shadow-xs"
              hide-default-footer
              :items-per-page="-1"
            >
              <template v-slot:no-data>
                <div class="pa-12 text-center grey--text lighten-2">
                  <v-icon x-large class="mb-4 opacity-20">fas fa-donate</v-icon>
                  <div class="text-subtitle-1 font-weight-bold grey--text">Sin costos adicionales</div>
                  <div class="caption grey--text opacity-70 mt-1">Haga clic en el menú superior para agregar un costo.</div>
                </div>
              </template>
              
              <template v-slot:item.name="{ item }">
                <div class="d-flex align-center py-2">
                  <v-avatar size="32" color="orange lighten-5" class="mr-3">
                    <v-icon x-small color="orange darken-2">fas fa-plus-circle</v-icon>
                  </v-avatar>
                  <span class="body-2 font-weight-black grey--text text--darken-3">{{ item.name }}</span>
                </div>
              </template>

              <template v-slot:item.price="{ item }">
                <div class="text-right">
                  <span class="body-1 font-weight-black success--text">{{ item.price | moneda }}</span>
                </div>
              </template>
              
              <template v-slot:item.actions="{ item }">
                <div class="d-flex justify-center">
                  <v-btn icon small color="error" class="grey lighten-4 shadow-sm" @click="deleteCost(item)">
                    <v-icon x-small>fas fa-trash-alt</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- Compras Tab -->
          <v-tab-item v-if="$can('read', 'PurchaseOrder')" class="pa-6">
            <purchase-orders :workorder="work_order" />
          </v-tab-item>
          
          <!-- Facturas Venta Tab -->
          <v-tab-item v-if="$can('read', 'Invoice')" class="pa-6">
            <invoices :workorder="work_order" />
          </v-tab-item>
          
          <!-- Facturas Compra Tab -->
          <v-tab-item v-if="$can('read', 'PurchaseInvoice')" class="pa-6">
            <purchase-invoices :workorder="work_order" />
          </v-tab-item>
          
          <!-- Resumen Tab -->
          <v-tab-item v-if="$can('readCost', 'SalesNote')" class="pa-6">
            <running-cost :work_order="work_order" />
          </v-tab-item>
          
          <!-- Documentos Tab -->
          <v-tab-item v-if="$can('read', 'WorkOrder')" class="pa-6">
            <file-manager
              :oldFiles="work_order.files.map(f => ({ ...f, delete: false }))"
              :sales_note="work_order.sales_note"
              @refresh="refreshWorkOrder"
            />
          </v-tab-item>

          <!-- Relbase Tab -->
          <v-tab-item v-if="relbaseEnabled" class="pa-6">
            <v-data-table
              :headers="relbaseDocumentsHeaders"
              :items="work_order.relbase_documents || []"
              class="modern-table elevation-0 border rounded"
              hide-default-footer
              :items-per-page="-1"
            >
              <template v-slot:no-data>
                <div class="pa-12 text-center grey--text">
                  <v-icon x-large class="mb-2 opacity-20" style="color:#35BDC8">fas fa-file-invoice-dollar</v-icon>
                  <div class="caption text-uppercase font-weight-bold">No hay documentos Relbase emitidos</div>
                </div>
              </template>
              <template v-slot:item.folio="{ item }">
                <span class="font-weight-black" style="color:#35BDC8">{{ item.folio }}</span>
              </template>
              <template v-slot:item.doc_type_display="{ item }">
                <v-chip x-small label outlined style="border-color:#35BDC8;color:#35BDC8;" class="font-weight-bold">{{ item.doc_type_display }}</v-chip>
              </template>
              <template v-slot:item.date_emited="{ item }">
                <span class="caption grey--text font-weight-medium">{{ item.date_emited | moment('DD/MM/YYYY') }}</span>
              </template>
              <template v-slot:item.total_bruto="{ item }">
                <span class="font-weight-black">{{ item.total_bruto | moneda }}</span>
              </template>
            </v-data-table>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </div>

    <!-- Modals -->
    <v-dialog v-model="addingCost" max-width="500" persistent>
      <cost-create-update @close="addingCost = false" @save="addCost" :custom="false" :global="false" :workOrder="true" :cost="newWorkOrderCost" :loading="costLoading" />
    </v-dialog>
    
    <confirm
      v-if="work_order"
      v-model="showConfirmCloseOrder" 
      message="¿Estás seguro que deseas marcar esta orden como FINALIZADA? Esto registrará los procesos pendientes."
      @ok="closeWorkOrder"
      @cancel="showConfirmCloseOrder = false"
    />

    <detail-prestages-dialog
      v-if="work_order"
      v-model="showProductModal"
      :product="productOnDisplay"
      :work-order-id="work_order.id"
    />
  </v-container>
</template>

<script>
import CostCreateUpdate from '@/components/products/costs/CostCreateUpdate'
import { updateWorkOrder, getWorkOrder } from '@/api/production'
import { getSalesNote } from '@/api/salesNotes'
import notifications from '@/utils/notifications'
import ProductDetail from './ProductDetail'
import RunningCost from './RunningCost'
import { postProcessOrderComplete } from '@/api/production'
import FileManager from '@/components/utils/FileManager'
import SoftnetDocumentsChips from '@/components/softnet/SoftnetDocumentsChips'
import KameDocumentsChips from '@/components/kame/KameDocumentsChips'
import SoftnetButton from '@/components/softnet/SoftnetButton.vue' 
import PrintWorkOrder from '@/components/production/PrintWorkOrder'
import Invoices from './Invoices.vue'
import PurchaseOrders from './PurchaseOrders.vue'
import PurchaseInvoices from './PurchaseInvoices.vue'
import DetailPrestagesDialog from '@/components/production/DetailPrestagesDialog'

export default {
  name: 'Detail',
  components: {
    CostCreateUpdate, ProductDetail, RunningCost,
    FileManager, SoftnetDocumentsChips, SoftnetButton, KameDocumentsChips,
    Invoices, PurchaseOrders, PurchaseInvoices, PrintWorkOrder, DetailPrestagesDialog
  },
  props: ['work_order', 'id', 'loading'],
  data() {
    return {
      productsTableHeaders: [
        { text: 'Código', value: 'library_product.sku', class: 'grey lighten-4', width: '120' },
        { text: 'Descripción del Producto', value: 'name', class: 'grey lighten-4' },
        { text: 'Cant.', value: 'qty', class: 'grey lighten-4', align: 'end', width: '80' },
        { text: 'Ancho', value: 'width', class: 'grey lighten-4', align: 'end', width: '80' },
        { text: 'Alto', value: 'height', class: 'grey lighten-4', align: 'end', width: '80' },
        { text: 'Estado', value: 'status', sortable: false, class: 'grey lighten-4', align: 'center', width: '150' },
        { text: 'Avance', value: 'progress', sortable: false, class: 'grey lighten-4', width: '150' },
        { text: '', value: 'data-table-expand', class: 'grey lighten-4', width: '50' }
      ],
      additionalCostsHeaders: [
        { text: 'Descripción del Gasto Adicional', value: 'name', class: 'grey lighten-5 font-weight-black py-4' },
        { text: 'Monto Neto', value: 'price', class: 'grey lighten-5 font-weight-black py-4', align: 'end', width: '200' },
        { text: '', value: 'actions', class: 'grey lighten-5', align: 'center', width: '80', sortable: false }
      ],
      addingCost: false,
      newWorkOrderCost: {
        id: -1, unit: '', name: '', price: 0, min_price: 0,
        is_supply: true, has_article: false, price_update_option: 0
      },
      costLoading: false,
      showConfirmCloseOrder: false,
      internalLoading: false,
      tab: null,
      expanded: [],
      showProductModal: false,
      productOnDisplay: { pre_stage_logs: [] },
      relbaseDocumentsHeaders: [
        { text: 'Folio', value: 'folio', class: 'grey lighten-4', width: '120' },
        { text: 'Tipo', value: 'doc_type_display', class: 'grey lighten-4' },
        { text: 'Fecha', value: 'date_emited', class: 'grey lighten-4', width: '140' },
        { text: 'Total', value: 'total_bruto', class: 'grey lighten-4', align: 'end', width: '150' }
      ]
    }
  },
  computed: {
    relbaseEnabled() {
      return !!this.$store.getters.getCompany?.relbase_integration_enabled
    },
    delayed() {
      if (!this.progressEstimated.estimated) return false
      return moment(this.progressEstimated.estimated).isAfter(moment(this.work_order.delivery_date))
    },
    additionalCosts() {
      if (!this.work_order || !this.work_order.additional_costs) return []
      return Array.isArray(this.work_order.additional_costs) ? this.work_order.additional_costs : []
    },
    has_prestage() {
      return this.work_order.products && this.work_order.products.some(p => p.in_prestage)
    },
    show_estimated_work_order() {
      return this.$store.getters.getCompany.show_estimated_work_order
    },
    progressEstimated() {
      if (!this.work_order || !this.work_order.products) return { progress: 0, estimated: 0 }
      
      let completedMts2 = 0
      let estimatedFinals = []
      
      this.work_order.products.forEach(p => {
        if (p.process_orders && p.process_orders.length > 0) {
          const estimateds = p.process_orders.map(proc => new Date(proc.estimated))
          if (estimateds.length) estimatedFinals.push(Math.max(...estimateds))
          
          // Granular progress calculation per product (average of process progresses)
          const totalProcProgress = p.process_orders.reduce((acc, proc) => {
            if (proc.completed) return acc + 100
            
            const target = proc.machine.production_unit_m2 
              ? (p.mts2 * proc.qtypp) 
              : (p.qty * proc.qtypp + (proc.merma || 0))
            
            const current = proc.machine.production_unit_m2 
              ? (proc.completed_mts2_qty.mts2 || 0) 
              : (proc.completed_mts2_qty.qty || 0)
            
            const procPercent = target > 0 ? (current / target) * 100 : 0
            return acc + Math.min(100, procPercent)
          }, 0)
          
          p.progress = totalProcProgress / p.process_orders.length
        } else {
          p.progress = 0
        }
        completedMts2 += (p.progress / 100) * (p.mts2 || 0)
      })
      
      return {
        estimated: estimatedFinals.length ? Math.max(...estimatedFinals) : 0,
        progress: this.work_order.total_mts2 > 0 ? (completedMts2 / this.work_order.total_mts2) * 100 : 0
      }
    }
  },
  methods: {
    showPrestageDetail(item) {
      this.productOnDisplay = item
      this.showProductModal = true
    },
    update() {
      this.$router.push({ name: 'WorkOrderUpdate', params: { id: this.work_order.id } })
    },
    async generateCostReport(item) {
      try {
        const data = await getSalesNote(item.sales_note)
        const { pdfCostReport } = await import('@/doc_generators/costReport')
        pdfCostReport(data)
      } catch (err) {
        notifications.showError('Error generando informe: ' + err)
      }
    },
    async deleteCost(item) {
      const idx = this.work_order.additional_costs.indexOf(item)
      this.work_order.additional_costs.splice(idx, 1)
      try {
        await updateWorkOrder({ id: this.work_order.id, additional_costs: this.work_order.additional_costs, justUpdateCost: true })
        notifications.showSuccess('Costo eliminado correctamente')
      } catch (e) {
        notifications.showError('Error al eliminar costo: ' + e)
        this.work_order.additional_costs.push(item)
      }
    },
    async addCost(item) {
      this.costLoading = true
      const cost = { name: item.name, price: item.price }
      
      if (!Array.isArray(this.work_order.additional_costs)) {
        this.$set(this.work_order, 'additional_costs', [])
      }
      this.work_order.additional_costs.push(cost)
      
      try {
        const res = await updateWorkOrder({ id: this.work_order.id, additional_costs: this.work_order.additional_costs, justUpdateCost: true })
        notifications.showSuccess('Costo agregado a la OT')
        this.work_order.additional_costs = res.additional_costs
        this.addingCost = false
        this.newWorkOrderCost = { id: -1, unit: '', name: '', price: 0, min_price: 0, is_supply: true }
        
        // Sincronización proactiva de todo el detalle
        await this.refreshWorkOrder()
      } catch (e) {
        notifications.showError('Error al guardar costo: ' + e)
        this.work_order.additional_costs.pop()
      } finally {
        this.costLoading = false
      }
    },
    async refreshWorkOrder() {
      this.internalLoading = true
      try {
        const data = await getWorkOrder(this.work_order.id)
        this.$emit('update:work_order', data)
        notifications.showSuccess('Información actualizada')
        await this.$store.dispatch('fetchWorkOrderResume', this.work_order.id)
      } catch (e) {
        notifications.showError('Error al actualizar OT: ' + e)
      } finally {
        this.internalLoading = false
      }
    },
    async closeWorkOrder() {
      this.showConfirmCloseOrder = false
      this.internalLoading = true
      
      try {
        const { products } = this.work_order
        for (const p of products) {
          if (p.process_orders) {
            for (const proc of p.process_orders) {
              if (!proc.completed) {
                if (proc.machine_equipments && proc.machine_equipments.length > 0) {
                  await postProcessOrderComplete({
                    processes: [{
                      id: parseInt(proc.id),
                      real_mts2: proc.bitacora.length === 0 ? parseFloat(p.mts2) : 0,
                      real_qty: proc.bitacora.length === 0 ? parseFloat(p.qty) : 0,
                      ask_completed: true,  
                    }],
                    equipment: proc.machine_equipments[0].id,
                    notes: 'Cierre automático de OT'
                  })
                }
              }
            }
          }
        }
        
        notifications.showSuccess('Orden de Trabajo Finalizada')
        this.$emit('close')
        setTimeout(() => { this.$emit('refresh') }, 1500)
      } catch (e) {
        notifications.showError('Error al cerrar orden: ' + e.message)
      } finally {
        this.internalLoading = false
      }
    }
  }
}
</script>

<style scoped>
.modern-card {
  border: 1px solid rgba(0,0,0,0.08) !important;
  border-radius: 12px !important;
  background-color: #fff;
}
.bg-primary-gradient {
  background: linear-gradient(135deg, var(--v-primary-base) 0%, var(--v-primary-darken1) 100%) !important;
}
.border-right-white { border-right: 1px solid rgba(255,255,255,0.2); }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.08); }
.bg-grey-light { background-color: #f8f9fa; }
.bg-success-light { background-color: rgba(var(--v-success-base), 0.05); }
.border-success { border: 1px solid rgba(var(--v-success-base), 0.1); }
.border-left-primary { border-left: 4px solid var(--v-primary-base) !important; }
.modern-detail-view { 
  position: relative;
  max-height: 90vh;
  overflow-y: auto;
  border-radius: 12px;
}
.modern-action-btn {
  background-color: #f8f9fa !important;
  border: 1px solid #eee !important;
  transition: all 0.2s;
}
.modern-action-btn:hover {
  background-color: #fff !important;
  border-color: var(--v-primary-base) !important;
  color: var(--v-primary-base) !important;
}
.lh-1-2 { line-height: 1.25; }
.pointer { cursor: pointer; }
.uppercase { text-transform: uppercase; letter-spacing: 0.5px; }
.sticky-progress { position: sticky; top: 0; z-index: 5; }
.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }
.gap-4 { gap: 16px; }
.opacity-50 { opacity: 0.5; }
.opacity-70 { opacity: 0.7; }
.shadow-sm { box-shadow: 0 2px 4px rgba(0,0,0,0.05) !important; }
.text-none { text-transform: none !important; }
.modern-detail-view >>> .v-tabs-bar { height: auto !important; }
.modern-detail-view >>> .v-window-item { overflow: visible !important; }
.expanded-row-container { background-color: #f1f3f5; }
.refresh-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 100;
  display: flex;
  justify-content: center;
  align-items: center;
}
.opacity-40 { opacity: 0.4; transition: opacity 0.3s ease; }
.pointer-events-none { pointer-events: none; }
.tracking-widest { letter-spacing: 2px; }
</style>